import { useCallback, useEffect, useRef, useState } from "react";

import {
  abandonGameSession,
  finishPracticeGame,
  fetchPracticeSession,
  startDuelSession,
  startPracticeGame,
  submitPracticeAttempt,
} from "./api";
import {
  AbandonActivityButton,
  AbandonedActivityPanel,
  ActivityGuide,
  defaultGuide,
  FeedbackNotice,
  RewardSummary,
  SessionProgressBar,
} from "./Experience";
import { navigate } from "./navigation";
import type {
  ExperienceFeedback,
  ExperienceGuide,
  ExperienceProgress,
  ExperienceReward,
  MemoDifficulty,
  MemoFace,
  MemoResult,
  PracticeGameSlug,
  PracticeQuestion,
  PracticeSession,
  UserScoreSnapshot,
} from "./types";

const gameLabels: Record<PracticeGameSlug, string> = {
  asso: "Asso",
  brio: "Brio",
  typo: "Typo",
  quizako: "Quizako",
};

const gameDescriptions: Record<PracticeGameSlug, string> = {
  asso: "Associez chaque recto à son verso sans phase de mémorisation.",
  brio: "Donnez le plus de bonnes réponses possible avant la fin du temps.",
  typo: "Saisissez une série complète de réponses justes sans rompre votre chaîne.",
  quizako: "Répondez à chaque question, en saisie libre, vrai/faux ou QCM.",
};

const cardCounts: Record<PracticeGameSlug, [number, number, number]> = {
  asso: [3, 4, 5],
  brio: [6, 8, 10],
  typo: [4, 6, 8],
  quizako: [6, 8, 10],
};

const difficultyKeys: MemoDifficulty[] = [
  "SOFT_DIFFICULTY",
  "NORMAL_DIFFICULTY",
  "HARD_DIFFICULTY",
];

export function FaceView({ face }: { face: MemoFace | undefined }) {
  if (!face) return <p className="face-missing">Face non renseignée.</p>;
  return (
    <div className="face-content">
      {face.text && <p className="face-text">{face.text}</p>}
      {face.image && <img src={face.image} alt={face.image_alt_text ?? ""} />}
      {face.audio && <audio controls preload="none" src={face.audio} />}
      {face.latex_text && <code className="latex-text">{face.latex_text}</code>}
      {face.latex_image && (
        <img src={face.latex_image} alt={face.latex_image_alt_text ?? "Formule"} />
      )}
      {face.description && <p className="face-description">{face.description}</p>}
    </div>
  );
}

function DifficultyPicker({
  game,
  value,
  onChange,
}: {
  game: PracticeGameSlug;
  value: MemoDifficulty;
  onChange: (difficulty: MemoDifficulty) => void;
}) {
  const labels = ["Facile", "Normale", "Difficile"];
  return (
    <fieldset>
      <legend>Difficulté</legend>
      {difficultyKeys.map((difficulty, index) => (
        <label key={difficulty}>
          <input
            type="radio"
            name={`${game}-difficulty`}
            checked={value === difficulty}
            onChange={() => onChange(difficulty)}
          />
          <span>{labels[index]} · jusqu’à {cardCounts[game][index]} cartes</span>
        </label>
      ))}
    </fieldset>
  );
}

function GameResultPanel({
  game,
  result,
  collectionId,
  duelId,
  replay,
  reward,
  guide,
}: {
  game: PracticeGameSlug;
  result: MemoResult;
  collectionId: number;
  duelId?: number;
  replay: () => void;
  reward?: ExperienceReward | null;
  guide?: ExperienceGuide;
}) {
  return (
    <div className={`memo-panel memo-result ${result.won ? "won" : "lost"}`}>
      <ActivityGuide guide={guide} activeStep={4} />
      <p className="eyebrow">{gameLabels[game]} · partie terminée</p>
      <h1>{result.won ? "Bravo !" : "Encore un essai"}</h1>
      <strong className="memo-score">{result.score} point{result.score > 1 ? "s" : ""}</strong>
      <p>Temps enregistré : {result.time} seconde{result.time > 1 ? "s" : ""}.</p>
      <RewardSummary reward={reward} />
      <div className="memo-result-actions">
        {!duelId && <button className="button button-primary" type="button" onClick={replay}>Rejouer</button>}
        <button className="button" type="button" onClick={() => navigate(duelId ? "/mes-duels" : `/series/${collectionId}`)}>
          {duelId ? "Voir le duel" : "Retour à la série"}
        </button>
      </div>
    </div>
  );
}

function PairGame({ collectionId, duelId, initialSessionToken, onScoreUpdated }: { collectionId: number; duelId?: number; initialSessionToken?: string; onScoreUpdated: (score: UserScoreSnapshot) => void }) {
  const game: PracticeGameSlug = "asso";
  const [difficulty, setDifficulty] = useState<MemoDifficulty>("SOFT_DIFFICULTY");
  const [session, setSession] = useState<PracticeSession | null>(null);
  const [successfulIds, setSuccessfulIds] = useState<number[]>([]);
  const [errors, setErrors] = useState(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [experienceFeedback, setExperienceFeedback] = useState<ExperienceFeedback | null>(null);
  const [progress, setProgress] = useState<ExperienceProgress | undefined>();
  const [reward, setReward] = useState<ExperienceReward | null>(null);
  const [result, setResult] = useState<MemoResult | null>(null);
  const [abandoned, setAbandoned] = useState<ExperienceFeedback | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef(0);
  const initialSessionLoaded = useRef(false);

  const begin = async (resumeToken?: string) => {
    setLoading(true);
    setError(null);
    try {
      const next = resumeToken
        ? await fetchPracticeSession(game, resumeToken)
        : duelId
        ? (await startDuelSession(duelId) as PracticeSession)
        : await startPracticeGame(game, collectionId, difficulty);
      setSession(next);
      setSuccessfulIds(next.successful_card_ids ?? []);
      setErrors(next.stats?.errors ?? 0);
      setFeedback(null);
      setResult(null);
      setExperienceFeedback(null);
      setProgress(next.progress);
      setReward(null);
      startedAt.current = Date.now();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible de démarrer Asso.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialSessionToken || initialSessionLoaded.current) return;
    initialSessionLoaded.current = true;
    void begin(initialSessionToken);
  }, [initialSessionToken]);

  const activeFront = session?.fronts?.find((item) => !successfulIds.includes(item.card_id));

  const choose = async (backCardId: number) => {
    if (!session || !activeFront || loading) return;
    setLoading(true);
    setError(null);
    try {
      const response = await submitPracticeAttempt(game, session.token, {
        front_card_id: activeFront.card_id,
        back_card_id: backCardId,
        elapsed_seconds: Math.floor((Date.now() - startedAt.current) / 1000),
      });
      setSuccessfulIds(response.successful_card_ids);
      setErrors(response.stats.errors);
      setFeedback(response.correct ? "Bonne association !" : "Ces deux faces ne correspondent pas.");
      setExperienceFeedback(response.feedback ?? null);
      setProgress(response.progress);
      setReward(response.reward ?? null);
      if (response.finished) {
        setResult(response.result);
        if (response.user_score) onScoreUpdated(response.user_score);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Réponse non enregistrée.");
    } finally {
      setLoading(false);
    }
  };

  const abandon = async () => {
    if (!session) return;
    setError(null);
    try {
      const response = await abandonGameSession(session.token);
      setAbandoned(response.feedback);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible d’interrompre l’activité.");
    }
  };

  if (abandoned) {
    return <AbandonedActivityPanel feedback={abandoned} onReturn={() => navigate(`/series/${collectionId}`)} />;
  }

  if (result) {
    return <GameResultPanel game={game} result={result} collectionId={collectionId} duelId={duelId} replay={() => setSession(null)} reward={reward} guide={session?.guide} />;
  }
  if (!session) {
    return (
      <div className="memo-panel memo-setup">
        <ActivityGuide guide={{ ...defaultGuide, target_count: cardCounts[game][difficultyKeys.indexOf(difficulty)] ?? 1 }} activeStep={0} />
        <p className="eyebrow">Moteur pédagogique</p>
        <h1>Asso</h1>
        <p className="lead">{gameDescriptions.asso}</p>
        {duelId ? (
          <div className="info-notice"><strong>Paramètres du duel verrouillés</strong><p>Vous jouez exactement le même tirage que votre adversaire.</p></div>
        ) : <DifficultyPicker game={game} value={difficulty} onChange={setDifficulty} />}
        {error && <p className="game-error">{error}</p>}
        <button className="button button-primary" type="button" disabled={loading} onClick={() => void begin()}>
          {loading ? "Préparation…" : "Commencer"}
        </button>
      </div>
    );
  }
  return (
    <div className="memo-panel">
      <ActivityGuide guide={session.guide} activeStep={experienceFeedback ? 3 : 2} />
      <SessionProgressBar progress={progress} />
      <div className="memo-status">
        <div><span>Paires trouvées</span><strong>{successfulIds.length}/{session.target_count}</strong></div>
        <div><span>Erreurs</span><strong>{errors}/3</strong></div>
      </div>
      {activeFront && (
        <>
          <div className="memo-question"><FaceView face={activeFront.face} /></div>
          <div className="memo-answers">
            {session.backs?.map((back) => (
              <button
                key={back.card_id}
                type="button"
                disabled={loading || successfulIds.includes(back.card_id)}
                onClick={() => void choose(back.card_id)}
              >
                <FaceView face={back.face} />
              </button>
            ))}
          </div>
        </>
      )}
      {!experienceFeedback && <p className={feedback?.startsWith("Bonne") ? "game-feedback success" : "game-feedback"}>{feedback}</p>}
      <FeedbackNotice feedback={experienceFeedback} />
      {error && <p className="game-error">{error}</p>}
      {!duelId && <AbandonActivityButton disabled={loading} onConfirm={abandon} />}
    </div>
  );
}

export function QuestionAnswer({
  question,
  text,
  selectedIds,
  disabled,
  setText,
  toggleId,
  submit,
}: {
  question: PracticeQuestion;
  text: string;
  selectedIds: number[];
  disabled: boolean;
  setText: (value: string) => void;
  toggleId: (id: number) => void;
  submit: (answer: unknown, responseMode?: "INPUT" | "QCM") => void;
}) {
  if (question.response_kind === "boolean") {
    return (
      <div className="boolean-answers">
        <button type="button" disabled={disabled} onClick={() => submit(true)}>Vrai</button>
        <button type="button" disabled={disabled} onClick={() => submit(false)}>Faux</button>
      </div>
    );
  }
  if (question.response_kind === "qcm") {
    return (
      <div className="qcm-answers">
        {question.answer_faces.map((face) => (
          <label key={face.id}>
            <input
              type="checkbox"
              checked={selectedIds.includes(face.id)}
              disabled={disabled}
              onChange={() => toggleId(face.id)}
            />
            <FaceView face={face} />
          </label>
        ))}
        <button className="button button-primary" type="button" disabled={disabled || selectedIds.length === 0} onClick={() => submit(selectedIds, "QCM")}>
          Valider
        </button>
      </div>
    );
  }
  const multiple = question.response_kind === "multiple_input";
  return (
    <div className="text-answer">
      {multiple ? (
        <textarea
          value={text}
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
          placeholder={`Une réponse par ligne (${question.answers_expected} attendues)`}
        />
      ) : (
        <input
          autoFocus
          value={text}
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && text.trim()) submit(text);
          }}
          placeholder="Votre réponse"
        />
      )}
      <button
        className="button button-primary"
        type="button"
        disabled={disabled || !text.trim()}
        onClick={() => submit(multiple ? text.split("\n").map((value) => value.trim()).filter(Boolean) : text, multiple ? "INPUT" : undefined)}
      >
        Valider
      </button>
    </div>
  );
}

function QuestionGame({ game, collectionId, duelId, initialSessionToken, onScoreUpdated }: { game: Exclude<PracticeGameSlug, "asso">; collectionId: number; duelId?: number; initialSessionToken?: string; onScoreUpdated: (score: UserScoreSnapshot) => void }) {
  const [difficulty, setDifficulty] = useState<MemoDifficulty>("SOFT_DIFFICULTY");
  const [session, setSession] = useState<PracticeSession | null>(null);
  const [cardIndex, setCardIndex] = useState(0);
  const [text, setText] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [errors, setErrors] = useState(0);
  const [answers, setAnswers] = useState(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [experienceFeedback, setExperienceFeedback] = useState<ExperienceFeedback | null>(null);
  const [progress, setProgress] = useState<ExperienceProgress | undefined>();
  const [reward, setReward] = useState<ExperienceReward | null>(null);
  const [result, setResult] = useState<MemoResult | null>(null);
  const [abandoned, setAbandoned] = useState<ExperienceFeedback | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gameSeconds, setGameSeconds] = useState(0);
  const [answerSeconds, setAnswerSeconds] = useState(0);
  const startedAt = useRef(0);
  const initialSessionLoaded = useRef(false);

  const elapsedSeconds = useCallback(
    () => Math.floor((Date.now() - startedAt.current) / 1000),
    [],
  );

  const begin = async (resumeToken?: string) => {
    setLoading(true);
    setError(null);
    try {
      const next = resumeToken
        ? await fetchPracticeSession(game, resumeToken)
        : duelId
        ? (await startDuelSession(duelId) as PracticeSession)
        : await startPracticeGame(game, collectionId, difficulty);
      setSession(next);
      const priorAttempts = next.attempts_count ?? next.stats?.answers ?? 0;
      const nextCardCount = next.cards?.length ?? 1;
      setCardIndex(priorAttempts % nextCardCount);
      setErrors(next.stats?.errors ?? 0);
      setAnswers(next.stats?.answers ?? 0);
      setFeedback(null);
      setResult(null);
      setExperienceFeedback(null);
      setProgress(next.progress);
      setReward(null);
      setGameSeconds(next.game_seconds ?? 0);
      setAnswerSeconds(next.answer_seconds ?? 0);
      startedAt.current = Date.now();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : `Impossible de démarrer ${gameLabels[game]}.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialSessionToken || initialSessionLoaded.current) return;
    initialSessionLoaded.current = true;
    void begin(initialSessionToken);
  }, [initialSessionToken]);

  const cards = session?.cards ?? [];
  const question = cards[cardIndex];

  const submitAnswer = useCallback(async (answer: unknown, responseMode?: "INPUT" | "QCM") => {
    if (!session || !question || loading) return;
    setLoading(true);
    setError(null);
    try {
      const response = await submitPracticeAttempt(game, session.token, {
        card_id: question.id,
        answer,
        response_mode: responseMode,
        elapsed_seconds: elapsedSeconds(),
      });
      setErrors(response.stats.errors);
      setAnswers(response.stats.answers);
      setFeedback(response.correct ? "Bonne réponse !" : "Réponse incorrecte.");
      setExperienceFeedback(response.feedback ?? null);
      setProgress(response.progress);
      setReward(response.reward ?? null);
      setText("");
      setSelectedIds([]);
      if (response.finished) {
        setResult(response.result);
        if (response.user_score) onScoreUpdated(response.user_score);
      } else if (game === "typo") {
        setCardIndex(response.correct ? (cardIndex + 1) % cards.length : 0);
      } else {
        setCardIndex((cardIndex + 1) % cards.length);
      }
      if (game === "brio") setAnswerSeconds(session.answer_seconds ?? 0);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Réponse non enregistrée.");
    } finally {
      setLoading(false);
    }
  }, [cardIndex, cards.length, elapsedSeconds, game, loading, onScoreUpdated, question, session]);

  const finishBrio = useCallback(async () => {
    if (game !== "brio" || !session || loading || result) return;
    setLoading(true);
    try {
      const response = await finishPracticeGame(game, session.token, elapsedSeconds());
      setErrors(response.stats.errors);
      setAnswers(response.stats.answers);
      setResult(response.result);
      setExperienceFeedback(response.feedback ?? null);
      setProgress(response.progress);
      setReward(response.reward ?? null);
      onScoreUpdated(response.user_score);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Fin de partie non enregistrée.");
    } finally {
      setLoading(false);
    }
  }, [elapsedSeconds, game, loading, onScoreUpdated, result, session]);

  const abandon = async () => {
    if (!session) return;
    setError(null);
    try {
      const response = await abandonGameSession(session.token);
      setAbandoned(response.feedback);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible d’interrompre l’activité.");
    }
  };

  useEffect(() => {
    if (game !== "brio" || !session || result || loading) return;
    if (gameSeconds <= 0) {
      void finishBrio();
      return;
    }
    if (answerSeconds <= 0 && question) {
      const emptyAnswer = question.response_kind === "boolean" ? null : question.response_kind === "input" ? "" : [];
      void submitAnswer(emptyAnswer, question.response_kind === "qcm" ? "QCM" : question.response_kind === "multiple_input" ? "INPUT" : undefined);
      return;
    }
    const timer = window.setTimeout(() => {
      setGameSeconds((value) => Math.max(0, value - 1));
      setAnswerSeconds((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [answerSeconds, finishBrio, game, gameSeconds, loading, question, result, session, submitAnswer]);

  if (abandoned) {
    return <AbandonedActivityPanel feedback={abandoned} onReturn={() => navigate(`/series/${collectionId}`)} />;
  }
  if (result) {
    return <GameResultPanel game={game} result={result} collectionId={collectionId} duelId={duelId} replay={() => setSession(null)} reward={reward} guide={session?.guide} />;
  }
  if (!session) {
    return (
      <div className="memo-panel memo-setup">
        <ActivityGuide guide={{ ...defaultGuide, target_count: cardCounts[game][difficultyKeys.indexOf(difficulty)] ?? 1, estimated_minutes: game === "brio" && difficulty === "SOFT_DIFFICULTY" ? 1 : 2 }} activeStep={0} />
        <p className="eyebrow">Moteur pédagogique</p>
        <h1>{gameLabels[game]}</h1>
        <p className="lead">{gameDescriptions[game]}</p>
        {duelId ? (
          <div className="info-notice"><strong>Paramètres du duel verrouillés</strong><p>Vous jouez exactement le même tirage que votre adversaire.</p></div>
        ) : <DifficultyPicker game={game} value={difficulty} onChange={setDifficulty} />}
        {error && <p className="game-error">{error}</p>}
        <button className="button button-primary" type="button" disabled={loading} onClick={() => void begin()}>
          {loading ? "Préparation…" : "Commencer"}
        </button>
      </div>
    );
  }
  return (
    <div className="memo-panel practice-panel">
      <ActivityGuide guide={session.guide} activeStep={experienceFeedback ? 3 : 2} />
      <SessionProgressBar progress={progress} />
      <div className="memo-status">
        <div><span>Bonnes réponses</span><strong>{answers}</strong></div>
        <div><span>Erreurs</span><strong>{errors}{game === "quizako" ? "" : "/3"}</strong></div>
        {game === "brio" && <div><span>Partie</span><strong>{gameSeconds}s</strong></div>}
        {game === "brio" && <div><span>Réponse</span><strong>{answerSeconds}s</strong></div>}
        {game !== "brio" && <div><span>Progression</span><strong>{Math.min(cardIndex + 1, cards.length)}/{cards.length}</strong></div>}
      </div>
      {question && (
        <>
          <div className="memo-question">
            <p className="card-side-label">Question</p>
            <FaceView face={question.front} />
          </div>
          <QuestionAnswer
            question={question}
            text={text}
            selectedIds={selectedIds}
            disabled={loading}
            setText={setText}
            toggleId={(id) => setSelectedIds((values) => values.includes(id) ? values.filter((value) => value !== id) : [...values, id])}
            submit={(answer, mode) => void submitAnswer(answer, mode)}
          />
        </>
      )}
      {!experienceFeedback && <p className={feedback?.startsWith("Bonne") ? "game-feedback success" : "game-feedback"}>{feedback}</p>}
      <FeedbackNotice feedback={experienceFeedback} />
      {error && <p className="game-error">{error}</p>}
      {!duelId && <AbandonActivityButton disabled={loading} onConfirm={abandon} />}
    </div>
  );
}

export function OtherGame({ game, collectionId, duelId, initialSessionToken, onScoreUpdated }: { game: PracticeGameSlug; collectionId: number; duelId?: number; initialSessionToken?: string; onScoreUpdated: (score: UserScoreSnapshot) => void }) {
  return (
    <section className="memo-page">
      <button className="back-button" type="button" onClick={() => navigate(duelId ? "/mes-duels" : `/series/${collectionId}`)}>
        ← Retour {duelId ? "aux duels" : "à la série"}
      </button>
      {game === "asso" ? <PairGame collectionId={collectionId} duelId={duelId} initialSessionToken={initialSessionToken} onScoreUpdated={onScoreUpdated} /> : <QuestionGame game={game} collectionId={collectionId} duelId={duelId} initialSessionToken={initialSessionToken} onScoreUpdated={onScoreUpdated} />}
    </section>
  );
}
